import React, { useState, useEffect, Suspense } from 'react';
import { useParams, useSearchParams, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  Loader2,
  Lock,
  RefreshCw
} from 'lucide-react';
import Tabs from '@shared/ui/Tabs';
import Button from '@shared/ui/Button';
import { WorkspaceConfiguration, WorkspacePermissions, WorkspaceTabProps } from '../../types/workspace';
import { getWorkspaceTabs } from './config/workspaceTabs';
import api from '../../lib/api';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { usePageMetadata } from '../../contexts/PageMetadataContext';
import { useFeatureFlags } from '@shared/flags/FeatureFlagProvider';

// Loading component for lazy-loaded tabs
const TabLoader: React.FC = () => (
  <div className="flex items-center justify-center py-12">
    <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
    <span className="ml-2 text-text-secondary dark:text-text-dark-secondary">Loading...</span>
  </div>
);

// Error boundary for tab components
/**
 * P2-11: takes the tab's DISPLAY LABEL, not its id. It used to render
 * "Error loading {tabId} tab", which showed users internal ids like
 * `call-to-action`, and told them to refresh — putting recovery on them for a
 * failure they can retry in place.
 */
const TabErrorBoundary: React.FC<{ children: React.ReactNode; tabId: string; tabLabel: string }> = ({
  children,
  tabId,
  tabLabel,
}) => {
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setHasError(false);
  }, [tabId]);

  if (hasError) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
        <AlertCircle className="h-6 w-6 text-error-600 dark:text-error-400" />
        <div>
          <p className="font-medium text-text-primary dark:text-text-dark-primary">
            {tabLabel} couldn&apos;t be loaded
          </p>
          <p className="mt-1 text-sm text-text-secondary dark:text-text-dark-secondary">
            Nothing you&apos;ve saved is affected. Try again, or come back in a moment.
          </p>
        </div>
        <Button variant="secondary" size="sm" icon={RefreshCw} onClick={() => setHasError(false)}>
          Try again
        </Button>
      </div>
    );
  }

  return <>{children}</>;
};

interface WorkspaceContainerProps {
  // No props needed - uses URL params
}

const WorkspaceContainer: React.FC<WorkspaceContainerProps> = () => {
  const { id: workspaceId } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('bills');
  // Mobile detection
  const isMobile = useMediaQuery('(max-width: 1023px)');
  const mobileSection = searchParams.get('section');
  
  // Page metadata
  const { setPageMetadata } = usePageMetadata();
  const { flags } = useFeatureFlags();

  // Fetch workspace configuration and permissions
  const { data: workspace, isLoading: workspaceLoading, error: workspaceError } = useQuery<WorkspaceConfiguration>({
    queryKey: ['workspace', workspaceId],
    queryFn: async () => {
      const { data } = await api.get(`/workspaces/${workspaceId}`);
      return data?.data || data;
    },
    enabled: !!workspaceId,
    retry: (failureCount, error: any) => {
      // Don't retry for 403 errors (access denied / inactive workspace)
      if (error?.response?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });

  // Fetch user permissions for this workspace
  const { data: rawPermissions, isLoading: permissionsLoading } = useQuery({
    queryKey: ['workspace-permissions', workspaceId],
    queryFn: async () => {
      const { data } = await api.get(`/workspaces/${workspaceId}/user-membership`);
      return data?.data || data;
    },
    enabled: !!workspaceId,
  });

  // Consume the capability flags computed by the server (single source of
  // truth). The /user-membership endpoint returns canView/canEdit/canManage/
  // canInvite/canDelete directly, so the client no longer re-derives them.
  const permissions: WorkspacePermissions | undefined = React.useMemo(() => {
    if (!rawPermissions) return undefined;

    return {
      canView: !!rawPermissions.canView,
      canEdit: !!rawPermissions.canEdit,
      canManage: !!rawPermissions.canManage,
      canInvite: !!rawPermissions.canInvite,
      canDelete: !!rawPermissions.canDelete,
      role: rawPermissions.role?.toLowerCase(),
    };
  }, [rawPermissions]);

  // Get available tabs based on workspace configuration
  const availableTabs = React.useMemo(() => {
    if (!workspace || !permissions) {
      return [];
    }

    const allTabs = getWorkspaceTabs();

    const filteredTabs = allTabs
      .filter(tab => {
        // Hide budget tab when flag is off (admins bypass)
        if (tab.id === 'budget' && !flags.budget_enabled && permissions.role !== 'system_admin') {
          return false;
        }

        // Check if tab is enabled in workspace settings OR base configuration
        const tabSettings = workspace.tabSettings?.[tab.id];

        // Use workspace settings if available, otherwise fall back to base tab config
        const isEnabled = tabSettings?.enabled ?? tab.enabled;

        if (!isEnabled) {
          return false;
        }

        // Check permissions
        if (tab.permissions && tab.permissions.length > 0) {
          const hasPermission = tab.permissions.some(permission =>
            permissions[permission as keyof WorkspacePermissions]
          );
          return hasPermission;
        }

        return true;
      })
      .filter(tab => {
        /*
          Hide Reports, Outreach, and Manage Workspace on mobile.

          P3-04 proposed surfacing these in an overflow menu instead, on the
          grounds that silently dropping a tab is indistinguishable from a bug.
          Confirmed intentional (2026-07-31) — these three are desk work, not
          phone work, and an overflow menu would advertise them without making
          them usable at that width. Left as-is deliberately; do not "fix".
        */
        if (isMobile) {
          const hiddenOnMobile = ['reports', 'call-to-action', 'manage'];
          return !hiddenOnMobile.includes(tab.id);
        }
        return true;
      })
      .sort((a, b) => {
        const aOrder = workspace.tabSettings?.[a.id]?.order ?? a.order;
        const bOrder = workspace.tabSettings?.[b.id]?.order ?? b.order;
        return aOrder - bOrder;
      });

    return filteredTabs;
  }, [workspace, permissions, isMobile, flags.budget_enabled]);

  /*
    The workspace names the page in the fixed header, like every other page
    (PageShell convention). #436 reported the name overlapping the page
    controls; the cause was Layout's hardcoded 64px top padding under a header
    that a logo + title + description makes ~100px tall, and 95b6a0fd worked
    around it by hiding the title here. eceaad71 fixed it properly — Layout
    measures the header and offsets <main> by it — so the title belongs back
    in the header, where the mobile chrome can show it too.
  */
  useEffect(() => {
    if (!workspace?.name) return;

    setPageMetadata({
      title: workspace.name,
      description: workspace.description || undefined,
      logoUrl: workspace.logoUrl || undefined,
      backUrl: '/workspaces',
    });

    return () => {
      setPageMetadata(null);
    };
  }, [workspace?.name, workspace?.description, workspace?.logoUrl, setPageMetadata]);

  // Set initial tab when workspace loads
  useEffect(() => {
    if (availableTabs.length > 0 && activeTab === 'bills') {
      // Only set on initial load (when activeTab is still the default)
      const tabParam = searchParams.get('tab');
      if (!tabParam) {
        setActiveTab(availableTabs[0].id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableTabs]); // Only run when availableTabs changes (initial load)

  // Handle URL parameters for tab switching. The tab param is kept in the URL
  // (not stripped) so history entries and back-links restore the correct tab —
  // e.g. returning from Outreach analytics to /workspaces/:id?tab=call-to-action.
  useEffect(() => {
    const tabParam = searchParams.get('tab');

    if (tabParam && availableTabs.some(tab => tab.id === tabParam)) {
      setActiveTab(tabParam);
    }
    // Don't reset to first tab when param is absent
  }, [searchParams, availableTabs]);

  // Tab bar items. The per-workspace custom label is resolved here rather than in
  // <Tabs> so the shared component stays unaware of workspace settings — and so
  // its width measurement sees the label the user will actually read.
  const tabItems = React.useMemo(
    () =>
      availableTabs.map((tab) => ({
        id: tab.id,
        label: workspace?.tabSettings?.[tab.id]?.customLabel || tab.label,
        icon: tab.icon,
      })),
    [availableTabs, workspace?.tabSettings]
  );

  // Handle tab change — mirror the active tab into the URL (replace, not push)
  // so the current history entry always restores to the tab the user was on
  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tabId);
    setSearchParams(newParams, { replace: true });
  };

  // Render active tab content
  const renderTabContent = () => {
    if (!workspace || !permissions) return null;

    const activeTabConfig = availableTabs.find(tab => tab.id === activeTab);
    if (!activeTabConfig) return null;

    const TabComponent = activeTabConfig.component;
    const tabProps: WorkspaceTabProps = {
      workspaceId: workspaceId!,
      workspace,
      permissions,
      onTabChange: handleTabChange,
      onRefresh: () => {
        // Trigger refetch of workspace data
        // This will be implemented with React Query invalidation
      }
    };

    return (
      <TabErrorBoundary tabId={activeTab} tabLabel={activeTabConfig.label}>
        {activeTabConfig.lazy ? (
          <Suspense fallback={<TabLoader />}>
            <TabComponent {...tabProps} />
          </Suspense>
        ) : (
          <TabComponent {...tabProps} />
        )}
      </TabErrorBoundary>
    );
  };

  // Loading state
  if (workspaceLoading || permissionsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        <span className="ml-3 text-lg text-text-secondary dark:text-text-dark-secondary">
          Loading workspace...
        </span>
      </div>
    );
  }

  // Error state - check for inactive workspace
  if (workspaceError || !workspace) {
    const error = workspaceError as any;
    const isInactive = error?.response?.status === 403 && error?.response?.data?.error === 'Workspace is not active';

    // Redirect to unavailable page for inactive workspaces
    if (isInactive) {
      return (
        <Navigate
          to="/workspace-unavailable"
          state={{
            message: 'This workspace is no longer available. It may have been deleted or deactivated by the workspace owner.',
          }}
          replace
        />
      );
    }

    /**
     * P2-11: a 403 and a 404 are different problems with different fixes, and
     * they used to share one message. "Doesn't exist or you don't have access"
     * leaves the one person who CAN fix it — a colleague who can add you — out of
     * the picture, so the user files a support ticket instead of sending a
     * message.
     */
    const isForbidden = error?.response?.status === 403;

    return (
      <div className="flex min-h-screen items-center justify-center px-6">
        <div className="flex max-w-md items-start gap-3">
          {isForbidden ? (
            <Lock className="mt-0.5 h-8 w-8 shrink-0 text-text-tertiary dark:text-text-dark-tertiary" />
          ) : (
            <AlertCircle className="mt-0.5 h-8 w-8 shrink-0 text-error-600 dark:text-error-400" />
          )}
          <div>
            <h2 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
              {isForbidden ? 'You don’t have access to this workspace' : 'Workspace not found'}
            </h2>
            <p className="mt-1 text-text-secondary dark:text-text-dark-secondary">
              {isForbidden
                ? 'This workspace exists, but you’re not a member. Ask one of its members or an organization admin to invite you.'
                : 'This workspace doesn’t exist. Check the link, or pick a workspace from your list.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Compute effective active tab - on mobile, use mobileSection directly
  const effectiveActiveTab = isMobile && mobileSection ? mobileSection : activeTab;

  // Render active tab content using effective active tab
  const renderMobileTabContent = () => {
    if (!workspace || !permissions) return null;

    const activeTabConfig = availableTabs.find(tab => tab.id === effectiveActiveTab);
    if (!activeTabConfig) return null;

    const TabComponent = activeTabConfig.component;
    const tabProps: WorkspaceTabProps = {
      workspaceId: workspaceId!,
      workspace,
      permissions,
      onTabChange: handleTabChange,
      onRefresh: () => {}
    };

    return (
      <TabErrorBoundary tabId={effectiveActiveTab} tabLabel={activeTabConfig.label}>
        {activeTabConfig.lazy ? (
          <Suspense fallback={<TabLoader />}>
            <TabComponent {...tabProps} />
          </Suspense>
        ) : (
          <TabComponent {...tabProps} />
        )}
      </TabErrorBoundary>
    );
  };

  // MOBILE: Redirect to bills if no section, otherwise show section content
  if (isMobile) {
    // If no section param, redirect to bills (default tab)
    if (!mobileSection) {
      return <Navigate to={`/workspaces/${workspaceId}?section=bills`} replace />;
    }

    // If has section param, show that section's content
    if (availableTabs.some(tab => tab.id === mobileSection)) {
      return (
        <div className="bg-background dark:bg-background-dark-primary min-h-screen">
          {/* Section Content - reduced padding for calendar tab to match main calendar */}
          <div className={effectiveActiveTab === 'calendar' ? 'pt-2 pb-4' : 'px-4 py-6'}>
            {renderMobileTabContent()}
          </div>
        </div>
      );
    }

    // Invalid section param, redirect to bills
    return <Navigate to={`/workspaces/${workspaceId}?section=bills`} replace />;
  }

  // DESKTOP: Tab-based navigation (workspace name/logo shown in the Layout header)
  return (
    <div className="bg-background dark:bg-background-dark-primary">
      {/* Tab Navigation */}
      <div className="bg-background dark:bg-background-dark-secondary">
        <div className="px-4 pt-2">
          <Tabs
            level="section"
            ariaLabel="Workspace sections"
            tabs={tabItems}
            activeId={activeTab}
            onChange={handleTabChange}
          />
        </div>
      </div>

      {/* Tab Content - reduced padding for calendar tab to match main calendar */}
      <div className={activeTab === 'calendar' ? 'pt-2 pb-4' : 'px-4 py-6'}>
        {renderTabContent()}
      </div>
    </div>
  );
};

export default WorkspaceContainer;
